import { InjectionToken, Provider } from '@angular/core';
import { Observable } from 'rxjs';

/**
 * Single hit shown in the global search autocomplete and on the result page.
 * Each provider maps its domain objects (shareable, clinic, code …) into this
 * generic shape so every surface can render a row with the same template:
 * a `title`, an optional `description`, and optional `tags`.
 */
export interface BdSearchResult {
  /** Stable identifier within the originating provider. */
  id: string;
  /** Primary headline of the row (e.g. the shareable's name). */
  title: string;
  /**
   * Optional secondary fact, shown on the row's meta line after the
   * {@link typeLabel}. For shareables this is the user's highest permission
   * (e.g. "Kann bearbeiten"); the resource *type* is deliberately not repeated
   * here, since {@link typeLabel} carries it and drives the type filter.
   */
  description?: string;

  /**
   * Optional "last changed" timestamp, rendered as the row's trailing meta
   * column. Providers whose domain object has no meaningful modification date
   * leave it undefined and the column stays empty.
   */
  modifiedDate?: Date;
  /**
   * Optional tag chips shown below the description. For shareables these are
   * the attached labels.
   */
  tags?: string[];
  /** Optional PrimeIcon class (e.g. "pi pi-file", "pi pi-building"). */
  icon?: string;
  /**
   * Optional badge label shown next to the title. This is the row's *type* in
   * user-facing wording ("Markt", "Klinik-Filter", "DRG") and doubles as the
   * grouping key of the result page's type filter — so it must vary per result
   * kind, not be a constant per provider.
   */
  typeLabel?: string;
  /**
   * Serialized in-app URL of the row's detail page (e.g. via
   * `BdShareableSelectService.buildDetailUrl`). When present, the result page
   * renders the row as a real `<a>` so right-click / middle-click / ⌘-click
   * open it like any other link. Leave undefined when the hit has no
   * navigable route.
   */
  url?: string;
  /**
   * Invoked when the user activates the row's open action. Remains the
   * fallback for surfaces that render results as a synthetic list and can't
   * host anchors (e.g. the autocomplete overlay).
   */
  open: () => void;
}

/**
 * Pluggable search provider. Apps register one provider per resource type
 * (shareables, clinics, codes …) via the BD_SEARCH_PROVIDERS multi-token.
 * Both the autocomplete and the result page fan out across all registered
 * providers and concatenate their hits in ascending `order`.
 */
export interface BdSearchProvider {
  /** Display order in the merged result list (lower = first). */
  readonly order: number;
  /** Returns matches for a trimmed (>=2 char) query, capped to ~50 hits. */
  search(query: string): Observable<BdSearchResult[]>;
}

export const BD_SEARCH_PROVIDERS = new InjectionToken<
  readonly BdSearchProvider[]
>('BD_SEARCH_PROVIDERS');

/**
 * Convenience for adding a single class-based provider to the multi-token.
 * Usage in bootstrap providers: `provideSearchProvider(MyClinicSearchProvider)`.
 */
export function provideSearchProvider(
  cls: new (...args: never[]) => BdSearchProvider,
): Provider {
  return { provide: BD_SEARCH_PROVIDERS, useClass: cls, multi: true };
}
